import type { ReactNode } from 'react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ReloadOutlined } from '@ant-design/icons';
import { ShieldAlert } from 'lucide-react';

import { PageHeader, TabsTruot } from '../../../components/shared';
import { Button } from '../../../components/ui';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { KhongCoQuyen } from '../components/chung';
import { TabCauHinhLoi } from '../components/cau-hinh-loi/TabCauHinhLoi';
import { TabKetNoi } from '../components/ket-noi/TabKetNoi';
import { TabTaiKhoanGui } from '../components/thong-bao/TabTaiKhoanGui';
import { TabThongBao } from '../components/thong-bao/TabThongBao';
import { QUYEN } from '../constants';
import { KHOA_RPA } from '../hooks/useRpa';
import { TAB_CAU_HINH, THAM_SO_TAB, tabMoSan } from '../nghiep-vu/tab';
import type { TabCauHinh } from '../nghiep-vu/tab';

const NHAN_TAB: Record<TabCauHinh, KhoaDich> = {
  errors: 'rpaCauHinh.tabLoi',
  notifications: 'rpaCauHinh.tabThongBao',
  senders: 'rpaCauHinh.tabTaiKhoanGui',
  connection: 'rpaCauHinh.tabKetNoi',
};

/** Dữ liệu làm mới theo tab đang mở. */
const KHOA_TAB: Record<TabCauHinh, readonly string[]> = {
  errors: KHOA_RPA.loi,
  notifications: KHOA_RPA.thongBao,
  senders: KHOA_RPA.thongBao,
  connection: KHOA_RPA.ketNoi,
};

/**
 * Cấu hình lỗi & thông báo (`/rpa/alert-config?tab=errors|notifications|senders|connection`, `RpaAlertConfigView` của
 * native). Native chặn cả màn theo cờ quản trị; ở đây mỗi tab theo quyền đọc riêng (`rpa:cau-hinh-loi:read`,
 * `rpa:thong-bao:read`, `rpa:tai-khoan-gui:read`, `rpa:ket-noi:read`), thiếu thì ẩn tab. Tab Kết nối OpenCore là mới.
 *
 * Tab đang mở ghi vào `?tab=` (thay chỗ, không thêm lịch sử), cùng cách với màn Gói & thanh toán: URL đổi từ ngoài
 * (liên kết "Mở Kết nối OpenCore" ở màn khác) thì dựng lại hàng tab theo URL.
 */
export function CauHinhLoiView() {
  const { t } = useT();
  const qc = useQueryClient();
  const quyen = useModulePermissions(QUYEN);
  const [thamSo, setThamSo] = useSearchParams();

  const docTab: Record<TabCauHinh, boolean> = {
    errors: quyen.cauHinhLoiDoc,
    notifications: quyen.thongBaoDoc,
    senders: quyen.taiKhoanGuiDoc,
    connection: quyen.ketNoiDoc,
  };
  const duocXem = TAB_CAU_HINH.filter((k) => docTab[k]);
  const moSan = tabMoSan(thamSo.get(THAM_SO_TAB), duocXem);

  const [daGhi, setDaGhi] = useState(moSan);
  const [lanDung, setLanDung] = useState(0);
  if (moSan !== daGhi) {
    setDaGhi(moSan);
    setLanDung((n) => n + 1);
  }

  const noiDung: Record<TabCauHinh, () => ReactNode> = {
    errors: () => <TabCauHinhLoi suaDuoc={quyen.cauHinhLoiSua} />,
    notifications: () => <TabThongBao suaDuoc={quyen.thongBaoSua} docProjectDuoc={quyen.projectDoc} />,
    senders: () => <TabTaiKhoanGui suaDuoc={quyen.taiKhoanGuiSua} docProjectDuoc={quyen.projectDoc} />,
    connection: () => <TabKetNoi suaDuoc={quyen.ketNoiSua} />,
  };

  const tieuDe = t('rpaCauHinh.tieuDe');

  return (
    <div className="space-y-4 p-4 lg:p-6">
      <PageHeader
        breadcrumb={[{ label: t('rpa.nhom') }, { label: tieuDe }]}
        titleIcon={<ShieldAlert size={18} className="text-primary-600" aria-hidden />}
        title={tieuDe}
        subtitle={t('rpaCauHinh.moTa')}
        actions={
          moSan ? (
            <Button
              variant="outline"
              size="md"
              icon={<ReloadOutlined />}
              onClick={() =>
                void qc.invalidateQueries({ queryKey: KHOA_TAB[moSan] })
              }
            >
              {t('chung.lamMoi')}
            </Button>
          ) : undefined
        }
      />

      {moSan === null ? (
        <KhongCoQuyen quyen={[QUYEN.cauHinhLoiDoc, QUYEN.thongBaoDoc, QUYEN.taiKhoanGuiDoc, QUYEN.ketNoiDoc].join(', ')} />
      ) : (
        <TabsTruot
          key={lanDung}
          xuongDong
          defaultActiveKey={moSan}
          onChange={(key) => {
            setDaGhi(key as TabCauHinh);
            setThamSo(
              (cu) => {
                const moi = new URLSearchParams(cu);
                moi.set(THAM_SO_TAB, key);
                return moi;
              },
              { replace: true },
            );
          }}
          items={duocXem.map((k) => ({ key: k, label: t(NHAN_TAB[k]), children: noiDung[k]() }))}
        />
      )}
    </div>
  );
}
